import { Component, OnInit, OnDestroy, Output, EventEmitter, Inject } from '@angular/core';
import { Dropin } from 'braintree-web-drop-in';
import { TranslateService } from '@ngx-translate/core';
import { BraintreeService, braintreeServiceToken } from 'go-modules/braintree/braintree.service';
import { BehaviorSubject, catchError, finalize, from, of, switchMap, take, tap } from 'rxjs';
import { NgxLoggerService } from '@go-modules/services/logger/logger.service';

@Component({
	selector: 'pay-with-card',
	templateUrl: 'pay-with-card.component.html',
	styleUrls: ['./pay-with-card.component.scss'],
	standalone: false
})
export class PayWithCardComponent implements OnInit, OnDestroy {
	@Output() public formLoaded = new EventEmitter<{ dropinInstance: Dropin }>();

	public loadingPayForm$ = new BehaviorSubject<boolean>(true);
	public payFormError$ = new BehaviorSubject<boolean>(false);
	public dropinInstance: Dropin;

	constructor (
		@Inject(braintreeServiceToken) private braintreeService: BraintreeService,
		private translateService: TranslateService,
		private logger: NgxLoggerService
	) {}

	public ngOnInit (): void {
		this.initBraintreeForm();
	}

	public ngOnDestroy (): void {
		if (this.dropinInstance?.teardown) {
			this.dropinInstance.teardown();
		}
	}

	private paymentFormLoaded (): void {
		this.formLoaded.emit({ dropinInstance: this.dropinInstance });
	}

	private initBraintreeForm (): void {
		const lang = this.translateService.getCurrentLang() || this.translateService.getFallbackLang();

		from(this.braintreeService.generateClientToken())
			.pipe(
				switchMap((clientToken) => {
					return from(this.braintreeService.createBraintreeForm({
						authorization: clientToken.token,
						container: '#braintree-v3-pay-form',
						locale: lang,
						vaultManager: true,
						card: {
							vault: {
								allowVaultCardOverride: true,
								vaultCard: true
							},
							overrides: {
								styles: {
									input: {
										'font-size': '14px',
										'color': '#555',
										'font-family': 'proxima-nova, "Open Sans", sans-serif'
									}
								}
							}
						}
					}));
				}),
				tap((dropin) => {
					this.dropinInstance = dropin;
					this.paymentFormLoaded();
				}),
				catchError((err) => {
					this.logger.error(err);
					this.payFormError$.next(true);
					return of(null);
				}),
				finalize(() => this.loadingPayForm$.next(false)),
				take(1)
			)
			.subscribe();
	}
}
